import { QueryClient, QueryKey } from "@tanstack/react-query";
import { TFunction } from "i18next";
import { I18nError } from "./I18nError";

/**
 * A device search filled a whole page of `limit` devices, so its result may be incomplete.
 * Deterministic: retrying gives the same result, only a narrower filter helps.
 */
export class DeviceCapError extends I18nError {
  constructor(readonly limit: number) {
    super(`The group filter matches ${limit} or more vehicles; narrow it down.`);
    this.name = "DeviceCapError";
  }

  i18nMessage(t: TFunction): string {
    return t("error.tooManyDevices", { count: this.limit });
  }
}

/** TanStack `retry` rule: a cap error is final, anything else gets the usual three attempts. */
export function retryUnlessDeviceCap(failureCount: number, error: Error): boolean {
  return !(error instanceof DeviceCapError) && failureCount < 3;
}

const notCapped = (query: { state: { error: unknown } }) => !(query.state.error instanceof DeviceCapError);

/**
 * `useQuery` options that make a cap error final for that key: no retry, no refetch on focus,
 * reconnect or mount, so the error stays visible instead of flipping back to loading. Any other
 * error is retried as usual, including on focus, reconnect and mount.
 */
export function deviceCapIsFinal(queryClient: QueryClient, queryKey: QueryKey) {
  return {
    retry: retryUnlessDeviceCap,
    // `retryOnMount` takes no function, so it is decided from the cached state at render.
    retryOnMount: !(queryClient.getQueryState(queryKey)?.error instanceof DeviceCapError),
    refetchOnWindowFocus: notCapped,
    refetchOnReconnect: notCapped,
  };
}
